import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { LanguageProvider } from '@/i18n/LanguageContext';
import Accounts from '@/pages/Accounts';
import Dashboard from '@/pages/Dashboard';
import Goals from '@/pages/Goals';
import Login from '@/pages/Login';
import Market from '@/pages/Market';
import NotFound from '@/pages/NotFound';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import Settings from '@/pages/Settings';
import TermsOfService from '@/pages/TermsOfService';
import Transactions from '@/pages/Transactions';
import Vault from '@/pages/Vault';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes';
import { Outlet, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { MainLayout } from './components/layout/MainLayout';

const queryClient = new QueryClient();

// Make sure this matches your Google Cloud Console client ID
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

// Log environment variables in development
if (import.meta.env.DEV) {
  console.log('Environment variables:', {
    GOOGLE_CLIENT_ID: GOOGLE_CLIENT_ID ? 'Set' : 'Not set',
    API_URL: import.meta.env.VITE_API_URL,
    USE_MOCK: import.meta.env.VITE_USE_MOCK,
  });
}

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <LanguageProvider>
          <TooltipProvider>
            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
              <Toaster />
              <Sonner />
              <Router>
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />

                  {/* Protected routes */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <Outlet />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Dashboard />} />
                    <Route path="accounts" element={<Accounts />} />
                    <Route path="goals" element={<Goals />} />
                    <Route path="goals/:goalId" element={<Goals />} />
                    <Route path="vault" element={<Vault />} />
                    <Route path="market" element={<Market />} />
                    <Route path="transactions" element={<Transactions />} />
                    <Route path="settings" element={<Settings />} />
                    <Route path="*" element={<NotFound />} />
                  </Route>
                </Routes>
              </Router>
            </GoogleOAuthProvider>
          </TooltipProvider>
        </LanguageProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
